/**
 * 下载按钮组件
 *
 * - 移动端：使用 Dialog 选择水印模式
 * - 桌面端：使用 HoverCard 选择水印模式
 */

import { sendGAEvent } from '@next/third-parties/google';
import * as Sentry from '@sentry/nextjs';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { BaseLineDownload } from '@/app/components/icons/BaseLineDownload';
import { BaseLineLoading1 } from '@/app/components/icons/BaseLineLoading1';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { DownloadWatermarkOption } from '@/app/components/shared/DownloadWatermarkOption';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { showUpgradeToast } from '@/app/components/shared/UpgradeToast';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import {
  HoverCard,
  HoverCardArrow,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@/app/components/ui/hover-card';
import { toast } from '@/app/components/ui/use-toast';
import { type CreationType, useDevice, useDownloadAlertPricing, useDownloadUrl, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ReportParams } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { downloadFile } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';

export const WATERMARK_STORAGE_KEY = 'VIDU_WATERMARK';

export type WatermarkMode = 'both' | 'watermarkOnly' | 'noWatermarkOnly';

interface DownloadButtonProps {
  creationType: CreationType;
  className?: string;
  waterMarkUri: string; // 默认下载链接，带水印
  noWaterMarkUri?: string; // 无水印下载链接
  downloadFileName?: string;
  watermarkMode?: WatermarkMode; // 水印模式：both-两个都显示，watermarkOnly-只显示水印选项，noWatermarkOnly-只显示无水印选项
  audioUri?: string; // 音频文件，无水印，需要前端拼接水印
  hasCopyright?: boolean; // 是否有版权限制，有版权限制时不允许去水印

  // 用于上报事件
  taskId: string;
  creationId: string;
  reportParams?: ReportParams;
  onHoverCardOpenChange?: (open: boolean) => void;
  container?: HTMLElement | null;
}

export const DownloadButton: FC<DownloadButtonProps> = ({
  creationType,
  className,
  waterMarkUri,
  noWaterMarkUri,
  downloadFileName,
  watermarkMode = 'both',
  audioUri,
  hasCopyright,

  taskId,
  creationId,
  reportParams,
  onHoverCardOpenChange,
  container,
}) => {
  const userInfo = useStore((state) => state.userInfo);
  const isFree = userInfo?.subs_plan === 'free';
  const { supportsHover } = useDevice();
  const t = useTranslations();
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const pathname = usePathname();
  const {
    withWatermark: rawWithWatermark,
    setWithWatermark,
    getDownloadUrls,
    loading: getDownloadUrlsLoading,
    canShowWatermarkInterstitialToday,
  } = useDownloadUrl();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { getIsAlertPricing, markAsShown } = useDownloadAlertPricing();

  useEffect(() => {
    // 免费用户 || 只有带水印的下载 => 默认选中带水印的选项
    if (isFree || watermarkMode === 'watermarkOnly') {
      setWithWatermark(true);
    }
    // 只有不带水印的下载 => 默认选中不带水印的选项
    if (watermarkMode === 'noWatermarkOnly') {
      setWithWatermark(false);
    }
  }, [isFree, watermarkMode]);

  const withWatermark = rawWithWatermark || hasCopyright;

  const handleDownload = async () => {
    reportEvent('operation', {
      interaction_type: withWatermark ? 'download_watermark' : 'download_no_watermark',
      interaction_position: pathname,
      task_id: taskId,
      creation_id: creationId,
      ...reportParams,
    });

    // 免费用户当日首次下载：提示升级 toast（原先直接弹订阅弹窗，因打断操作改为 toast）
    if (getIsAlertPricing() && isFree) {
      showUpgradeToast({
        message: t('payments.plans.toastNoWatermark'),
        actionText: t('payments.plans.upgradeNow'),
        onAction: () =>
          openPlanDetailDialog({ scene: PlanDialogScene.Download, creationId, withWatermark, fromPage: 'download' }),
      });
      markAsShown();
      reportEvent('vip_price_toast_exposure', {
        from_page: 'download',
      });
      sendGAEvent('event', 'subscription_exposure', {
        from_page: 'download',
      });
    }
  };

  const handleDownloadError = (errorInfo: Record<string, unknown>) => {
    Sentry.captureException(new Error('Download URL missing'), {
      tags: {
        'creation.operation': 'download',
        'creation.type': creationType || 'unknown',
      },
      extra: errorInfo,
      level: 'error',
    });

    reportEvent('operation', {
      interaction_type: 'download_failed',
      interaction_position: pathname,
      task_id: taskId,
      creation_id: creationId,
      ...reportParams,
    });

    toast({
      type: 'error',
      message: t('creations.batchOperation.downloadFail'),
    });
  };

  // 只在移动端弹窗中点击下载按钮时使用
  const handleDialogDownload = async () => {
    handleDownload();

    // 在弹窗中点击下载按钮时，手动触发下载
    const downloadUrls = await getDownloadUrls([
      {
        creationType: creationType,
        creationId: creationId,
        originalUri: creationType === 'audio' ? audioUri || waterMarkUri : waterMarkUri,
        nomarkUri: noWaterMarkUri,
        hasCopyright: hasCopyright,
      },
    ]);

    const targetUrl = downloadUrls[0]?.url;
    if (!targetUrl) {
      handleDownloadError({
        creationId,
        creationType,
        waterMarkUri,
        noWaterMarkUri,
        watermarkMode,
        isFree,
        withWatermark,
        hasCopyright,
      });
      setDialogOpen(false);
      return;
    }

    downloadFile(targetUrl, downloadFileName || '', {
      autoRevokeBlobUrl: !!audioUri && withWatermark,
    });

    // 关闭对话框
    setDialogOpen(false);
  };

  const runDownload = async () => {
    // 如果正在加载双水印资源，则不进行下载
    if (getDownloadUrlsLoading) return;
    // 移动端且支持双选项，打开弹窗选择
    if (!supportsHover && watermarkMode === 'both') {
      setDialogOpen(true);
      return;
    }
    // 获取下载URL并触发下载
    const downloadUrls = await getDownloadUrls([
      {
        creationType: creationType,
        creationId: creationId,
        originalUri: creationType === 'audio' ? audioUri || waterMarkUri : waterMarkUri,
        nomarkUri: noWaterMarkUri,
        hasCopyright: hasCopyright,
      },
    ]);
    const targetUrl = downloadUrls[0]?.url;
    if (!targetUrl) {
      handleDownloadError({
        creationId,
        creationType,
        waterMarkUri,
        noWaterMarkUri,
        watermarkMode,
        isFree,
        withWatermark,
        hasCopyright,
      });
      return;
    }
    downloadFile(targetUrl, downloadFileName || '', { autoRevokeBlobUrl: true });
    setTimeout(() => handleDownload(), 1000);
  };

  const useAnchor = withWatermark && canShowWatermarkInterstitialToday;
  const buttonClassName = cn('flex cursor-pointer items-center justify-center rounded-8', className);
  const content = getDownloadUrlsLoading ? <BaseLineLoading1 className="animate-spin" /> : <BaseLineDownload />;

  const renderDownloadButton = () => {
    const onClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      runDownload();
    };
    if (useAnchor) {
      return (
        <a href={pathname ?? '/'} className={buttonClassName} aria-label="Download" onClick={onClick}>
          {content}
        </a>
      );
    }
    return (
      <div className={buttonClassName} aria-label="Download" onClick={onClick}>
        {content}
      </div>
    );
  };

  // 只下载不带水印的文件
  if (watermarkMode === 'noWatermarkOnly') {
    return (
      <CustomTooltip content={t('creation.action.download')} container={container}>
        {renderDownloadButton()}
      </CustomTooltip>
    );
  }

  // 只下载带水印的文件
  if (watermarkMode === 'watermarkOnly') {
    return (
      <CustomTooltip content={t('creation.action.download')} container={container}>
        {renderDownloadButton()}
      </CustomTooltip>
    );
  }

  // 不支持 hover 的设备使用 Dialog（移动端设备）
  if (!supportsHover) {
    return (
      <>
        {renderDownloadButton()}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="w-[320px] p-4 sm:gap-0">
            <div className="mb-4 text-center font-semibold text-lg">
              {t('sharedComponent.downloadButton.downloadOptions')}
            </div>
            <DownloadWatermarkOption
              isFree={isFree}
              withWatermark={withWatermark}
              setWithWatermark={setWithWatermark}
              hasCopyright={hasCopyright}
              creationId={creationId}
            />
            <Button variant="primary" className="mt-4 w-full" onClick={handleDialogDownload}>
              {t('creation.action.download')}
            </Button>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // 支持 hover 的设备使用 HoverCard（桌面端设备）
  return (
    <HoverCard openDelay={0} closeDelay={100} onOpenChange={onHoverCardOpenChange}>
      <HoverCardTrigger asChild>{renderDownloadButton()}</HoverCardTrigger>
      <HoverCardPortal container={container || document.body}>
        <HoverCardContent className="w-fit bg-system-bg02 p-1 text-sm" align="center" sideOffset={2} side="top">
          <DownloadWatermarkOption
            isFree={isFree}
            withWatermark={withWatermark}
            setWithWatermark={setWithWatermark}
            hasCopyright={hasCopyright}
            creationId={creationId}
          />
          <HoverCardArrow />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
